import { LogicDeps } from '@chegg-wt/core/state'
import { AppState, FSACreatorPayload } from '@chegg-wt/core/state/types'
import { createLogic } from 'redux-logic'
import { GatedBibliographyOnExpirationModalData } from '@chegg-wt/core/state/bibliography/types'
import { Order, OrderBy } from '@chegg-wt/core/globalTypes'
import { getProjectsExperience } from '@chegg-wt/core/utils/citationFlow'
import { CITATIONS_DASHBOARD } from '@chegg-wt/core/constants/citationFlow'
import { GatedStyleRuleTypes } from '@chegg-wt/core/constants/styles'
import { setGuestProjectId } from '@chegg-wt/core/utils/localStorage'
import { currentUrlStyleNameSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { setGenerateNewCitationList } from '@chegg-wt/core/state/currentProject/actions'

import {
  getActualStyleId,
  isStyleIdInValid,
  popularStyles,
  styleIdToAlias,
  transformDeprecatedStyleId,
} from '@chegg-wt/core/utils/styles'
import { UserStyles } from '@chegg-wt/core/utils/userStyles'
import { UserRoles } from '@chegg-wt/core/constants/user'
import {
  AUTH_MODAL_TITLE,
  AuthModalOrigin,
} from '@chegg-wt/core/components/Modals/AuthModal/types'
import { StyledGateModalVariant } from '@chegg-wt/core/components/Modals/constants'
import { Config } from '@chegg-wt/core/config'
import {
  GATED_BIBLIOGRAPHY_ON_EXPIRATION_MODAL,
  CHANGE_TO_FREE_STYLE_MODAL,
  UPGRADE_MODAL,
  ONE_AUTH_MODAL,
  NEW_CITATION_LIST_MODAL,
} from '../modals/constants'
import { jwtInitialized, navigated, flowed } from '../app/actions'
import { fetchCitations } from '../bibliography/actions'
import { citationCreate, citationCreateFailed } from '../citation/actions'
import { openModal } from '../modals/actions'
import {
  userRoleSelector,
  userProjectIdSelector,
  isProSelector,
  isGuestSelector,
  jwtSelector,
  loggedInSelector,
} from '../user/selectors'
import { authModalExpFlagSelector } from '../createCitation/selectors'
import { modalTypeSelector } from '../modals/selectors'
import { ActionTypes, DEFAULT_PROJECT_NAME } from './constants'
import { FetchCitationsPayload } from './types'
import {
  fetchCurrentProjectStyleName,
  fetchUrlStyleName,
  fetchProjectStyleNameError,
  fetchUrlStyleNameError,
  setCurrentProjectStyleName,
  setUrlStyleName,
  setAsCurrentProject,
  createProject,
  editCitationStyleFailed,
  setCurrentProject,
  setCurrentProjectInProgress,
  fetchProjectError,
  styleSelected,
  softGateStyle,
  checkProjectExpiration,
} from './actions'
import {
  citationNameSelector,
  currentProjectSelector,
  currentStyleSelector,
  currentProjectIdSelector,
  generateNewCitationListSelector,
  hasExpiredSelector,
  isCurrentGuestGatedStyleSelector,
} from './selectors'

export const createProjectLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof createProject>,
  any,
  LogicDeps
>({
  type: [createProject],
  async process({ getState, api, action }, dispatch, done) {
    const state = getState()
    const currentDate = new Date().toLocaleDateString()
    const shouldProjectBePublic = /* getState().user.loggedIn === false */ true
    let style = currentStyleSelector(state)
    const currentUrlName = currentUrlStyleNameSelector(state)
    const isGuest = isGuestSelector(state)
    const { sessionId } = jwtSelector(state)
    if (generateNewCitationListSelector(getState())) {
      dispatch(setGenerateNewCitationList(false))
      let newUrlStyle = ''
      if (currentUrlName) newUrlStyle = getActualStyleId(currentUrlName.toLowerCase())
      style = newUrlStyle
    }
    try {
      const project = await api.bff.createProject({
        name: `${DEFAULT_PROJECT_NAME} ${currentDate}`,
        defaultStyle: style,
        public: shouldProjectBePublic,
      })

      dispatch(setCurrentProject(project))
      dispatch(fetchCurrentProjectStyleName(project.defaultStyle))
      if (isGuest) {
        setGuestProjectId(sessionId, project.id)
      }

      const { citationData, annotationText, shouldFetchCitations } = action.payload
      if (citationData) {
        dispatch(citationCreate(citationData, annotationText))
      }
      if (shouldFetchCitations) {
        dispatch(setCurrentProject(project))
        dispatch(fetchCurrentProjectStyleName(project.defaultStyle))
        dispatch(fetchCitations(project.id))
      }
    } catch (error) {
      // dispatch(displayError(ErrorMsgs.CREATE_PROJECT_ERROR, error))
      dispatch(citationCreateFailed(error))
    } finally {
      done()
    }
  },
})

export const fetchCurrentProjectStyleNameLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof fetchCurrentProjectStyleName>,
  any,
  LogicDeps
>({
  type: [ActionTypes.FETCH_PROJECT_STYLE_NAME],
  async process({ getState, api, action }, dispatch, done) {
    const { style } = action.payload
    const result = styleIdToAlias(style)
    if (result !== style) {
      const formattedResult =
        result === 'ama' || result === 'apa6'
          ? result.toUpperCase()
          : result.charAt(0).toUpperCase() + result.slice(1)
      dispatch(setCurrentProjectStyleName(formattedResult))
      return done()
    }
    const selectorResult = citationNameSelector(getState())
    if (selectorResult) {
      dispatch(setCurrentProjectStyleName(selectorResult))
      return done()
    }
    try {
      const styleName = (await api.oneGraph.getStylesV2({ keyword: style })).data
        .citationStylesV2
      dispatch(setCurrentProjectStyleName(styleName.results[0].name))
    } catch (error) {
      dispatch(fetchProjectStyleNameError(error))
    } finally {
      done()
    }
  },
})

export const fetchUrlStyleNameLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof fetchUrlStyleName>,
  any,
  LogicDeps
>({
  type: [ActionTypes.FETCH_URL_STYLE_NAME],
  async process({ api, action }, dispatch, done) {
    const { style } = action.payload
    const pop = popularStyles[style]
    if (pop) {
      const [styleName] = pop
      dispatch(setUrlStyleName(styleName))
      return done()
    }

    try {
      const styleName = (await api.oneGraph.getStylesV2({ keyword: style })).data
        .citationStylesV2

      dispatch(setUrlStyleName(styleName.results[0].name))
    } catch (error) {
      dispatch(fetchUrlStyleNameError(error))
    } finally {
      done()
    }
  },
})

function getProjectIdFromPayloadOrState(action, getState) {
  const state = getState()

  /**
   * we'll always prefer the id from the payload, indicating the page was loaded with a projectId
   * Then the project from the /token response if there is an active project
   * finally, from the existing ID in the currentProject redux slice
   */
  const projectId =
    action.payload.projectId ||
    userProjectIdSelector(state) ||
    currentProjectIdSelector(state)
  return projectId
}

export const fetchProjectLogic = createLogic<
  AppState,
  FetchCitationsPayload,
  any,
  LogicDeps
>({
  type: [ActionTypes.FETCH_CURRENT_PROJECT],
  async process({ action, getState, api, routes }, dispatch, done) {
    const projectId = getProjectIdFromPayloadOrState(action, getState)

    if (!projectId) {
      dispatch(navigated(routes.toCitationsHome()))
      return done()
    }
    try {
      const project = await api.bff.getProject({ projectId })
      dispatch(setCurrentProject(project))
      dispatch(fetchCurrentProjectStyleName(project.defaultStyle))
      dispatch(fetchCitations(projectId))
      dispatch(checkProjectExpiration())
    } catch (error) {
      dispatch(fetchProjectError(error))
    } finally {
      done()
    }
  },
})

export const fetchProjectErrorLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof fetchProjectError>,
  any,
  LogicDeps
>({
  type: [fetchProjectError],
  validate({ getState, action, routes }, allow, reject) {
    const isLoggedIn = loggedInSelector(getState())
    if (!isLoggedIn) {
      return reject(navigated(routes.toLogin()))
    }
    return allow(action)
  },
  process({ routes }) {
    const nextRoute =
      getProjectsExperience() === CITATIONS_DASHBOARD
        ? routes.toCitationsDashboard()
        : routes.toFolders()
    return navigated(nextRoute)
  },
})

export const softGateStyleLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof softGateStyle>,
  any,
  LogicDeps
>({
  type: [ActionTypes.SOFT_GATE_STYLE],
  process({ action, getState }, dispatch, done) {
    const state = getState()
    const userRole = userRoleSelector(state)

    // check if gated style rule applies
    const userStyles = new UserStyles(userRole)
    const currentStyle = currentStyleSelector(state)
    const style = transformDeprecatedStyleId(action.payload.style || currentStyle)
    const { callbackFn, redirect } = action.payload
    const isStyleAllowed = userStyles.isStyleAllowed(style)

    const isGatedToAllowedStyle =
      isStyleAllowed && isCurrentGuestGatedStyleSelector(state)
    // eslint-disable-next-line no-undef-init
    let modalVariant = undefined
    // guest user going from gated style to free style
    if (isGatedToAllowedStyle) {
      modalVariant = StyledGateModalVariant.CHANGE_STYLE
    }

    if (
      userRole === UserRoles.FREE_USER &&
      !userStyles.isStyleAllowed(currentStyle) &&
      userStyles.isStyleAllowed(style)
    ) {
      modalVariant = StyledGateModalVariant.NEW_CITATION_LIST_MODAL
    }

    switch (modalVariant) {
      case StyledGateModalVariant.CHANGE_STYLE:
        dispatch(
          openModal({
            name: CHANGE_TO_FREE_STYLE_MODAL,
            data: {
              changeToStyle: style,
              callbackFn,
              redirect,
            },
          })
        )
        done()
        break
      case StyledGateModalVariant.NEW_CITATION_LIST_MODAL:
        dispatch(
          openModal({
            name: NEW_CITATION_LIST_MODAL,
            data: {
              withAuthBanner: true,
              alwaysParticipate: false,
            },
          })
        )
        done()
        break

      default: {
        dispatch(
          openModal({
            name: UPGRADE_MODAL,
            data: {
              alwaysParticipate: false,
              withAuthBanner: true,
            },
          })
        )
        done()
      }
    }
  },
})

export const editProjectStyleLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof styleSelected>,
  any,
  LogicDeps
>({
  type: [ActionTypes.STYLE_SELECTED],
  validate({ getState, action, routes }, allow, reject) {
    const { style: payloadStyle, skipSoftGate, allowStyleChange } = action.payload
    const state = getState()
    const projectId = currentProjectIdSelector(state)
    const currentStyle = currentStyleSelector(state)
    const style = transformDeprecatedStyleId(payloadStyle)
    const didStyleChange = currentStyle !== style

    const userRole = userRoleSelector(state)
    const authModalExpFlag = authModalExpFlagSelector(state)
    // check if gated style rule applies
    const userStyles = new UserStyles(userRole)
    const gatedStyleRuleType = userStyles.getGatedStyleRuleType()
    // check style payload is gated, and current style is gated
    const isStyleGated = !userStyles.isStyleAllowed(style)
    const isCurrentStyleGated = !userStyles.isStyleAllowed(currentStyle)
    // transform action to protect against deprecated styles
    action.payload.style = style // eslint-disable-line no-param-reassign

    if (Config.enableStyleGate && allowStyleChange === true && projectId)
      return allow(action)

    if (Config.enableStyleGate && !isStyleGated && isCurrentStyleGated) {
      return reject(softGateStyle(payloadStyle))
    }

    if (
      Config.enableStyleGate &&
      !isStyleIdInValid(payloadStyle) &&
      isCurrentGuestGatedStyleSelector(state)
    ) {
      // guest gated to free case
      return reject(softGateStyle(payloadStyle))
    }
    // if gated style, reject the request by the gated type
    if (isStyleGated) {
      switch (gatedStyleRuleType) {
        case GatedStyleRuleTypes.SOFT_ACCOUNT_GATE:
          if (Config.enableStyleGate && !isStyleIdInValid(payloadStyle)) {
            return reject(softGateStyle(payloadStyle))
          }
          if (skipSoftGate === true) return reject(undefined)
          break
        default: {
          if (authModalExpFlag && userRole === UserRoles.NOT_LOGGED_IN) {
            return reject(
              openModal({
                name: ONE_AUTH_MODAL,
                data: {
                  defaultFlow: 'signup',
                  origin: AuthModalOrigin.BIBLIOGRAPHY_PAGE,
                  redirectTo: routes.toUpgrade(),
                  title: AUTH_MODAL_TITLE,
                },
              })
            )
          }
          return reject(flowed('toUpgrade'))
        }
      }
    }
    // if style hasnt changed we dont want to run the process hook
    if (!projectId || !didStyleChange) return reject(action)
    return allow(action)
  },
  async process({ api, action, getState, routes }, dispatch, done) {
    const state = getState()
    const project = currentProjectSelector(state)
    const isChangeToFreeStyleModal =
      modalTypeSelector(state) === CHANGE_TO_FREE_STYLE_MODAL
    const { style, redirect = true } = action.payload
    try {
      const response = await api.bff.editProject({
        projectId: project.id,
        name: project.name,
        defaultStyle: style,
        public: !!project.public,
      })
      if (!isChangeToFreeStyleModal && redirect) {
        dispatch(
          navigated(routes.toBibliography({ style, projectId: project.id }), {
            replace: true,
          })
        )
      }
      dispatch(setCurrentProject(response))
      dispatch(fetchCurrentProjectStyleName(response.defaultStyle))
      if (redirect) dispatch(fetchCitations(project.id))
    } catch (e) {
      dispatch(editCitationStyleFailed(e))
    } finally {
      done()
    }
  },
})

export const getActiveProjectLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof jwtInitialized>,
  any,
  LogicDeps
>({
  type: jwtInitialized,
  async process({ api }, dispatch, done) {
    const projects = await api.bff.getProjects({
      limit: 1,
      orderBy: OrderBy.updatedAt,
      order: Order.desc,
    })

    if (projects?.length) {
      const project = projects[0]
      dispatch(setCurrentProject(project))
      dispatch(fetchCurrentProjectStyleName(project.defaultStyle))
      dispatch(fetchCitations(project.id))
    }
    dispatch(setCurrentProjectInProgress({ status: false }))
    done()
  },
})

export const checkProjectExpirationLogic = createLogic<AppState, any, any, LogicDeps>({
  type: [checkProjectExpiration],
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const isPro = isProSelector(state)
    const hasExpired = hasExpiredSelector(state)
    if (isPro || !hasExpired) {
      reject(undefined)
    } else {
      allow(action)
    }
  },
  process(_, dispatch, done) {
    dispatch(
      openModal({
        name: GATED_BIBLIOGRAPHY_ON_EXPIRATION_MODAL,
        data: {
          isDismissible: false,
          shouldContinueToFolders: true,
        } as GatedBibliographyOnExpirationModalData,
      })
    )
    done()
  },
})

export const setAsCurrentProjectLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof setAsCurrentProject>,
  any,
  LogicDeps
>({
  type: ActionTypes.SET_AS_CURRENT_PROJECT,
  async process({ api, action }) {
    const { projectId } = action.payload
    const project = await api.bff.visitProject({ projectId })
    return setCurrentProject(project)
  },
})
